<script setup lang="ts">
import { computed } from 'vue'
import type { SearchFilters } from '~~/shared/search-constants'
import {
  ESTABLISHMENT_FILTER_I18N_KEYS,
  ESTABLISHMENT_FILTER_ICONS,
  SEJOUR_SLUGS,
  type SejourSlug,
} from '~~/shared/establishment-filters'

/**
 * Séjours types en cases à cocher : étape Envies du parcours d'accueil
 * (`HoliHomeSearchWizard`), toujours en 56 px. Même patron que
 * `HoliAmenityChips` : composant sans état propre, la liste cochée vit chez
 * l'appelant (`v-model`).
 *
 * Vraies cases à cocher (`<input type="checkbox">` dans un `<label>`) : chaque
 * séjour est atteignable au clavier, annoncé coché ou non, et l'anneau de
 * focus est porté par l'étiquette visible.
 */
const props = withDefaults(defineProps<{
  modelValue: readonly SejourSlug[]
  /** Type de la recherche : `table-dhotes` n'a de sens qu'en chambre d'hôtes. */
  type?: SearchFilters['type']
  /** Légende du groupe ; sans elle, l'appelant nomme le groupe autrement. */
  legend?: string
  /** `aria-labelledby` du groupe (ex. la question de l'étape Envies). */
  labelledby?: string
  /** `aria-describedby` du groupe (ex. l'indice de l'étape Envies). */
  describedby?: string
  /** Préfixe i18n des libellés (`<préfixe>.<clé ESTABLISHMENT_FILTER_I18N_KEYS>`). */
  labelPrefix?: string
}>(), {
  type: undefined,
  legend: undefined,
  labelledby: undefined,
  describedby: undefined,
  labelPrefix: 'pages.home.wizard.envies.options',
})

const emit = defineEmits<{
  'update:modelValue': [value: SejourSlug[]]
}>()

const { t } = useI18n()

// `table-dhotes` visible en chambre d'hôtes, ou s'il est coché : le cacher
// laisserait un critère compté mais impossible à décocher.
const items = computed(() => SEJOUR_SLUGS
  .filter(slug =>
    slug !== 'table-dhotes'
    || props.type === 'chambre-hotes'
    || props.modelValue.includes('table-dhotes'),
  )
  .map(slug => ({
    slug,
    icon: ESTABLISHMENT_FILTER_ICONS[slug],
    label: t(`${props.labelPrefix}.${ESTABLISHMENT_FILTER_I18N_KEYS[slug]}`),
  })))

function isChecked(slug: SejourSlug): boolean {
  return props.modelValue.includes(slug)
}

/** Bascule un séjour ; la liste émise suit l'ordre de `SEJOUR_SLUGS`. */
function toggle(slug: SejourSlug, checked: boolean) {
  const next = new Set(props.modelValue)
  if (checked) next.add(slug)
  else next.delete(slug)
  emit('update:modelValue', SEJOUR_SLUGS.filter(s => next.has(s)))
}

/**
 * Case contrôlée : la case native reprend l'état du `modelValue` ; seul le
 * retour du parent (v-model) la coche réellement.
 */
function onChange(slug: SejourSlug, event: Event) {
  const input = event.target as HTMLInputElement
  const checked = input.checked
  input.checked = isChecked(slug)
  toggle(slug, checked)
}
</script>

<template>
  <fieldset
    class="min-w-0"
    :aria-labelledby="labelledby"
    :aria-describedby="describedby"
  >
    <legend
      v-if="legend"
      class="mb-3 text-[17px] font-medium text-paper-900"
    >
      {{ legend }}
    </legend>
    <div class="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      <label
        v-for="item in items"
        :key="item.slug"
        :class="[
          'flex min-h-14 items-center gap-3 rounded-lg border-[1.5px] px-4 text-[17px] leading-tight cursor-pointer transition-colors',
          'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-sage-700',
          isChecked(item.slug)
            ? 'bg-sage-700 border-sage-700 text-paper-50'
            : 'bg-paper-100 border-sage-500 text-paper-900 hover:bg-sage-100',
        ]"
      >
        <input
          type="checkbox"
          class="sr-only"
          :value="item.slug"
          :checked="isChecked(item.slug)"
          @change="onChange(item.slug, $event)"
        >
        <UIcon
          :name="item.icon"
          class="size-[22px] shrink-0"
          aria-hidden="true"
        />
        <span class="flex-1 min-w-0 break-words">{{ item.label }}</span>
        <UIcon
          v-if="isChecked(item.slug)"
          name="i-lucide-check"
          class="size-5 shrink-0"
          aria-hidden="true"
        />
      </label>
    </div>
  </fieldset>
</template>
